class DinoChatbot extends HTMLElement {
  constructor() {
    super();
    this.products = [];
    this.conversation = [];
    this.lastRecommendedHandles = [];
    this.isReady = false;
    this.viewportFrame = null;
  }

  connectedCallback() {
    if (this.isReady) return;

    this.launcher = this.querySelector('.dino-chatbot__launcher');
    this.panel = this.querySelector('.dino-chatbot__panel');
    this.closeButton = this.querySelector('.dino-chatbot__close');
    this.messages = this.querySelector('[data-dino-messages]');
    this.form = this.querySelector('[data-dino-form]');
    this.input = this.form?.querySelector('input[name="message"]');
    this.sendButton = this.form?.querySelector('button[type="submit"]');
    this.suggestions = this.querySelector('[data-dino-suggestions]');
    this.nudge = this.querySelector('[data-dino-nudge]');
    this.nudgeOpen = this.querySelector('[data-dino-nudge-open]');
    this.nudgeClose = this.querySelector('[data-dino-nudge-close]');
    this.products = this.readProducts();
    this.knowledgeBase = this.readKnowledge();
    // The Liquid catalogue below is enough for an immediate fallback. Fetch the
    // live catalogue only when a visitor actually opens Dino.
    this.productLoadPromise = null;
    this.endpoint = this.dataset.dinoEndpoint?.trim();

    this.knowledge = this.buildKnowledge();
    this.bindEvents();
    this.observeStickyAddToCart();
    this.observeVisualViewport();
    this.scheduleNudge();
    this.isReady = true;
  }

  readProducts() {
    const dataElement = this.querySelector('[data-dino-products]');
    if (!dataElement) return [];

    try {
      return JSON.parse(dataElement.textContent || '[]').filter((product) => product && product.handle);
    } catch (error) {
      return [];
    }
  }

  readKnowledge() {
    const dataElement = this.querySelector('[data-dino-knowledge]');
    if (!dataElement) return '';

    try {
      return String(JSON.parse(dataElement.textContent || '""') || '').trim();
    } catch (error) {
      return '';
    }
  }

  async loadLiveProducts() {
    const cacheKey = 'first-fill-dino-products-v1';
    const cacheLifetime = 15 * 60 * 1000;

    try {
      const cached = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
      if (
        cached
        && Array.isArray(cached.products)
        && cached.createdAt > Date.now() - cacheLifetime
      ) {
        this.products = cached.products;
        return;
      }

      const response = await fetch('/collections/all/products.json?limit=250');
      const payload = response.ok ? await response.json() : { products: [] };
      const products = (payload.products || [])
        .map((product) => ({
          handle: product.handle,
          title: product.title,
          url: `/products/${product.handle}`,
          image: product.images?.[0]?.src || '',
          price: this.formatPrice(product.variants?.[0]?.price),
          variantId: product.variants?.[0]?.id,
          available: Boolean(product.variants?.some((variant) => variant.available)),
          oneLiner: String(product.body_html || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 160),
          description: String(product.body_html || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 450),
          details: '',
          variants: (product.variants || []).map((variant) => ({ title: variant.title, available: variant.available })),
        }))
        .filter((product) => product.handle);

      if (products.length > 0) {
        this.products = products;
        sessionStorage.setItem(cacheKey, JSON.stringify({ createdAt: Date.now(), products }));
      }
    } catch (error) {
      // Keep the Liquid-rendered catalogue as a resilient fallback.
    }
  }

  ensureLiveProducts() {
    if (!this.productLoadPromise) {
      this.productLoadPromise = this.loadLiveProducts();
    }

    return this.productLoadPromise;
  }

  formatPrice(value) {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return '';
    return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(amount);
  }

  bindEvents() {
    this.launcher?.addEventListener('click', () => this.open());
    this.closeButton?.addEventListener('click', () => this.close());
    this.nudgeOpen?.addEventListener('click', () => this.open());
    this.nudgeClose?.addEventListener('click', (event) => {
      event.stopPropagation();
      this.hideNudge(true);
    });

    this.form?.addEventListener('submit', (event) => {
      event.preventDefault();
      const message = this.input?.value.trim();
      if (!message) return;

      this.input.value = '';
      this.handleUserMessage(message);
    });

    this.suggestions?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-dino-prompt]');
      if (!button) return;
      this.handleUserMessage(button.dataset.dinoPrompt);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !this.panel?.hidden) this.close();
    });
  }

  open() {
    if (!this.panel) return;

    this.ensureLiveProducts();

    this.panel.hidden = false;
    this.classList.add('is-open');
    document.documentElement.classList.add('dino-chat-is-open');
    this.hideNudge(true);
    this.launcher?.setAttribute('aria-expanded', 'true');

    if (!this.messages?.dataset.initialized) {
      this.addMessage(
        'bot',
        'Hi, ik ben Dino. Stel gerust je vraag over autoverzorging of een First Fill product.'
      );
      this.messages.dataset.initialized = 'true';
    }

    this.updateVisualViewport();

    if (!window.matchMedia('(max-width: 749px)').matches) {
      setTimeout(() => this.input?.focus({ preventScroll: true }), 80);
    }
  }

  close() {
    if (!this.panel) return;

    this.panel.hidden = true;
    this.classList.remove('is-open');
    document.documentElement.classList.remove('dino-chat-is-open');
    this.launcher?.setAttribute('aria-expanded', 'false');
  }

  observeVisualViewport() {
    const viewport = window.visualViewport;
    if (!viewport) {
      this.updateVisualViewport();
      return;
    }

    const scheduleUpdate = () => {
      if (this.viewportFrame) window.cancelAnimationFrame(this.viewportFrame);
      this.viewportFrame = window.requestAnimationFrame(() => this.updateVisualViewport());
    };

    viewport.addEventListener('resize', scheduleUpdate, { passive: true });
    viewport.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('orientationchange', scheduleUpdate, { passive: true });
    this.updateVisualViewport();
  }

  updateVisualViewport() {
    const viewport = window.visualViewport;
    const height = viewport?.height || window.innerHeight;
    const offsetTop = viewport?.offsetTop || 0;

    this.style.setProperty('--dino-visual-height', `${Math.round(height)}px`);
    this.style.setProperty('--dino-visual-top', `${Math.round(offsetTop)}px`);
  }

  scheduleNudge() {
    if (!this.nudge) return;

    const page = this.readPageContext();
    const productName = page.productTitle || this.formatProductHandle(page.productHandle);
    const text = productName ? `Heb je een vraag over ${productName}?` : 'Waarmee kan ik je helpen?';

    if (this.nudgeOpen) this.nudgeOpen.textContent = text;

    window.setTimeout(() => {
      if (this.panel && !this.panel.hidden) return;
      this.nudge.hidden = false;
    }, 25000);
  }

  hideNudge() {
    if (this.nudge) this.nudge.hidden = true;
  }

  formatProductHandle(handle) {
    if (!handle) return '';
    return handle
      .split('-')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  observeStickyAddToCart() {
    const stickyBar = document.querySelector('.sticky-add-to-cart__bar');
    if (!stickyBar) return;

    const updateOffset = () => {
      const isStuck = stickyBar.dataset.stuck === 'true';
      const barHeight = stickyBar.getBoundingClientRect().height || 0;
      const baseOffset = window.matchMedia('(max-width: 749px)').matches ? 14 : 18;
      const offset = isStuck ? Math.ceil(barHeight + 34) : baseOffset;

      this.style.setProperty('--dino-bottom-offset', `${offset}px`);
    };

    updateOffset();

    const observer = new MutationObserver(updateOffset);
    observer.observe(stickyBar, {
      attributes: true,
      attributeFilter: ['data-stuck'],
    });

    window.addEventListener('resize', updateOffset, { passive: true });
  }

  async handleUserMessage(message) {
    if (this.isSending) return;

    this.setSending(true);
    await this.ensureLiveProducts();
    this.open();
    this.classList.add('has-conversation');
    this.addMessage('user', message);
    this.rememberMessage('user', message);

    const thinking = this.addTypingIndicator();

    if (this.endpoint) {
      try {
        const result = await this.askAi(message);
        thinking?.remove();

        const reply = [result.reply, result.followUp].filter(Boolean).join(' ');
        const botMessage = reply ? this.addMessage('bot', reply) : null;
        if (result.products.length > 0) this.addProducts(result.products, botMessage);
        this.rememberAssistantResult({ ...result, reply, followUp: '' });
        if (!reply && result.products.length === 0) this.addFallbackReply(message);
        this.setSending(false);
        return;
      } catch (error) {
        thinking?.remove();
      }
    } else {
      thinking?.remove();
    }

    this.addFallbackReply(message);
    this.setSending(false);
  }

  setSending(isSending) {
    this.isSending = isSending;
    if (this.sendButton) this.sendButton.disabled = isSending;
    this.messages?.setAttribute('aria-busy', isSending ? 'true' : 'false');
  }

  addFallbackReply(message) {
    const packageFormats = this.findPackageFormats(message);
    if (packageFormats) {
      this.addMessage('bot', packageFormats);
      this.rememberMessage('assistant', packageFormats);
      return;
    }

    const packageAvailability = this.findPackageAvailability(message);
    if (packageAvailability) {
      const botMessage = this.addMessage('bot', packageAvailability.reply);
      if (packageAvailability.products.length > 0) this.addProducts(packageAvailability.products, botMessage);
      this.rememberAssistantResult({ ...packageAvailability, followUp: '' });
      return;
    }

    const suitabilityReply = this.buildSuitabilityFallback(message);
    if (suitabilityReply) {
      this.addMessage('bot', suitabilityReply);
      this.rememberMessage('assistant', suitabilityReply);
      return;
    }

    const result = this.findProducts(message);

    if (result.products.length === 0) {
      const reply =
        'Ik kan hier het beste advies op geven als ik iets meer context heb. Wat wil je precies schoonmaken of verbeteren, en wat zie je nu gebeuren?';
      this.addMessage('bot', reply);
      this.rememberMessage('assistant', reply);
      return;
    }

    const botMessage = this.addMessage('bot', result.reply);
    this.addProducts(result.products, botMessage);
    this.rememberAssistantResult({
      reply: result.reply,
      products: result.products,
      followUp: '',
    });
  }

  findPackageFormats(message) {
    const text = this.normalize(message);
    const asksFormats = /\b(formaat|formaten|inhoud|inhouden|maat|maten|verpakkingen|verpakking)\b/.test(text);
    if (!asksFormats) return '';

    const formats = this.products
      .flatMap((product) => product.variants || [])
      .filter((variant) => variant?.available)
      .map((variant) => this.extractVolume(variant.title))
      .filter(Boolean);
    const uniqueFormats = [...new Set(formats)].sort((first, second) => this.volumeInMillilitres(first) - this.volumeInMillilitres(second));

    if (uniqueFormats.length === 0) {
      return 'Ik zie op dit moment geen specifieke formaten bij de online producten. Kijk gerust op een productpagina voor de actuele variantkeuze.';
    }

    return `De online beschikbare formaten zijn: ${uniqueFormats.join(', ')}.`;
  }

  extractVolume(value) {
    const match = String(value || '').match(/\b(\d+(?:[.,]\d+)?)\s*(ml|l|liter)\b/i);
    if (!match) return '';

    const amount = match[1].replace('.', ',');
    const unit = match[2].toLowerCase();
    return unit === 'ml' ? `${amount} ml` : `${amount} L`;
  }

  volumeInMillilitres(value) {
    const match = String(value || '').match(/(\d+(?:[.,]\d+)?)\s*(ml|l)/i);
    if (!match) return Number.MAX_SAFE_INTEGER;

    const amount = Number(match[1].replace(',', '.'));
    return match[2].toLowerCase() === 'l' ? amount * 1000 : amount;
  }

  findPackageAvailability(message) {
    const text = this.normalize(message);
    const askedForFiveLitres = /\b(5\s?l|5\s?liter|vijf\s?liter)\b/.test(text);
    const asksAvailability = /\b(hebben jullie|hebben we|verkopen jullie|is er|zijn er|bestaat er|beschikbaar|jerrycan|jerrycans|fles|flessen)\b/.test(text);

    if (!askedForFiveLitres || !asksAvailability) return null;

    const fiveLitreProducts = this.products.filter((product) => {
      const productText = this.normalize(
        [
          product.title,
          product.oneLiner,
          product.description,
          product.details,
          ...(product.variants || []).map((variant) => variant?.title),
        ]
          .filter(Boolean)
          .join(' ')
      );
      return /\b(5\s?l|5\s?liter|vijf\s?liter)\b/.test(productText);
    });

    if (fiveLitreProducts.length === 0) {
      return {
        products: [],
        reply: 'Ik zie op dit moment geen 5 L-variant die online bestelbaar is. Zodra die live in de webshop staat, kan ik hem direct voor je tonen.',
      };
    }

    return {
      products: fiveLitreProducts.slice(0, 2),
      reply: 'Ja, we hebben 5 L-jerrycans. Dit zijn twee passende opties uit de webshop:',
    };
  }

  async askAi(message) {
    const response = await fetch(this.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        message,
        products: this.products,
        knowledge: this.knowledgeBase,
        page: this.readPageContext(),
        conversation: this.conversation.slice(-8),
        lastRecommendedHandles: this.lastRecommendedHandles,
      }),
    });

    if (!response.ok) throw new Error('Dino AI request failed');

    const data = await response.json();
    const products = this.productsByHandle(data.product_handles || []);

    return {
      reply: String(data.reply || '').trim(),
      products,
      followUp: String(data.follow_up || '').trim(),
    };
  }

  productsByHandle(handles) {
    if (!Array.isArray(handles)) return [];

    const productMap = new Map(this.products.map((product) => [product.handle, product]));
    return handles.map((handle) => productMap.get(handle)).filter(Boolean);
  }

  readPageContext() {
    const productMatch = window.location.pathname.match(/\/products\/([^/?#]+)/);
    const currentHandle = productMatch?.[1] || '';
    const currentProduct = this.products.find((product) => product.handle === currentHandle);

    return {
      url: window.location.href,
      title: document.title,
      productHandle: currentHandle,
      productTitle: currentProduct?.title || '',
    };
  }

  rememberMessage(role, text) {
    const cleanText = String(text || '').trim();
    if (!cleanText) return;

    this.conversation.push({
      role,
      text: cleanText.slice(0, 700),
    });

    this.conversation = this.conversation.slice(-10);
  }

  rememberAssistantResult(result) {
    const parts = [result.reply, result.followUp].filter(Boolean);
    if (parts.length > 0) this.rememberMessage('assistant', parts.join(' '));

    if (result.products?.length > 0) {
      this.lastRecommendedHandles = result.products.map((product) => product.handle).filter(Boolean).slice(0, 3);
    }
  }

  buildSuitabilityFallback(message) {
    const text = this.normalize(message);
    const asksSuitability = /\b(geschikt|kan dit|kan deze|past dit|past deze|veilig voor|werkt dit|werkt deze)\b/.test(text);
    const mentionsCarModel = /\b(audi|bmw|mercedes|volkswagen|vw|ford|opel|toyota|tesla|volvo|kia|hyundai|renault|peugeot|citroen|skoda|seat|mazda|nissan|fiat|mini|porsche|serie|klasse|a3|a4|a5|q3|q5|b180|golf|polo|model 3)\b/.test(text);

    if (!asksSuitability && !mentionsCarModel) return '';

    const contextProducts = this.getContextProducts();
    if (contextProducts.length > 0) {
      const productNames = contextProducts.map((product) => product.title).join(', ');
      return `Ja, ${productNames} kun je in principe gebruiken voor je auto. Bij was-, lak-, glas-, interieur- en velgproducten kies je vooral op het onderdeel dat je wilt reinigen of beschermen, niet op automodel. Test bij gevoelige materialen wel altijd eerst klein en uit het zicht.`;
    }

    return 'Voor je auto zijn de meeste First Fill producten voor wassen, glas, velgen, interieur en lak gewoon geschikt. Je kiest vooral op wat je wilt aanpakken: vuil op de lak, remstof op velgen, strepen op glas of vlekken in het interieur. Alleen bij brandstofadditieven is je motortype wel belangrijk.';
  }

  getContextProducts() {
    const productMap = new Map(this.products.map((product) => [product.handle, product]));
    const recommended = this.lastRecommendedHandles.map((handle) => productMap.get(handle)).filter(Boolean);
    if (recommended.length > 0) return recommended;

    const page = this.readPageContext();
    const currentProduct = productMap.get(page.productHandle);
    return currentProduct ? [currentProduct] : [];
  }

  findProducts(message) {
    const text = this.normalize(message);
    const scored = this.products
      .map((product) => {
        const knowledge = this.knowledge[product.handle] || {};
        const keywords = knowledge.keywords || [];
        const score = keywords.reduce((total, keyword) => {
          if (text.includes(keyword)) return total + 4;
          return total;
        }, 0) + this.fuzzyScore(text, product.title);

        return {
          product,
          score,
          priority: knowledge.priority || 0,
        };
      })
      .filter((item) => item.score > 0)
      .sort((a, b) => b.score - a.score || b.priority - a.priority)
      .map((item) => item.product);

    const recommended = this.dedupe(scored).slice(0, 1);

    if (recommended.length === 0) {
      return { products: [], reply: '' };
    }

    const main = recommended[0];
    const reason = this.knowledge[main.handle]?.reason || main.oneLiner;

    return {
      products: recommended,
      reply: `Ik zou beginnen met ${main.title}. ${reason}`,
    };
  }

  fuzzyScore(text, title) {
    return this.normalize(title)
      .split(' ')
      .filter((part) => part.length > 3 && text.includes(part)).length;
  }

  dedupe(products) {
    const seen = new Set();
    return products.filter((product) => {
      if (seen.has(product.handle)) return false;
      seen.add(product.handle);
      return true;
    });
  }

  buildKnowledge() {
    return {
      'rim-cleaner': {
        priority: 95,
        reason: 'Die is gemaakt voor remstof, ijzerdeeltjes en hardnekkig velgvuil.',
        keywords: ['velg', 'velgen', 'velgenreiniger', 'velg reiniger', 'rim cleaner', 'remstof', 'wiel', 'wielen', 'paars', 'ijzer', 'brake', 'wheel'],
      },
      'car-shampoo': {
        priority: 90,
        reason: 'Sterk voor een grondige wasbeurt met rijk schuim en een lakvriendelijke formule.',
        keywords: ['shampoo', 'wassen', 'wasbeurt', 'auto wassen', 'auto vies', 'vieze auto', 'mijn auto is vies', 'spons', 'washandschoen', 'schuim', 'vuil', 'vies', 'exterieur'],
      },
      'wash-wax': {
        priority: 88,
        reason: 'Handig als je reinigen en extra glans in een stap wilt combineren.',
        keywords: ['wash wax', 'wax', 'wassen en glans', 'glans na wassen', 'snel wassen', 'auto wassen', 'auto vies', 'vieze auto', 'beschermen tijdens wassen'],
      },
      'foam-cleaner': {
        priority: 86,
        reason: 'Perfect als je met actief schuim wilt voorwassen en vuil veilig wilt losweken.',
        keywords: ['foam', 'snow foam', 'schuim', 'voorwas', 'hogedruk', 'hoge druk', 'foamen', 'foam cleaner', 'auto vies', 'vieze auto', 'hardnekkig vuil'],
      },
      'glass-cleaner': {
        priority: 85,
        reason: 'Die helpt voor helder glas zonder strepen of vettige waas.',
        keywords: ['glas', 'glasreiniger', 'glass cleaner', 'ramen', 'ruit', 'ruiten', 'voorruit', 'streep', 'strepen', 'zicht', 'spiegel'],
      },
      'quick-polish': {
        priority: 84,
        reason: 'Goed voor snelle glans, een glad oppervlak en een nette showroom finish.',
        keywords: ['glans', 'lak glans', 'showroom', 'polish', 'booster', 'gloss', 'quick polish', 'snel glanzend'],
      },
      'ceramic-sealant': {
        priority: 83,
        reason: 'Kies deze als je vooral bescherming, waterafstoting en een gladde finish zoekt.',
        keywords: ['ceramic', 'keramisch', 'beschermen', 'bescherming', 'waterafstotend', 'shield', 'coating', 'druppels'],
      },
      'surface-protect': {
        priority: 82,
        reason: 'Een goede keuze voor bescherming op oppervlakken die langer schoon moeten blijven.',
        keywords: ['surface', 'protect', 'oppervlak beschermen', 'beschermlaag', 'vuilafstotend', 'waterafstotend'],
      },
      'interior-cleaner': {
        priority: 80,
        reason: 'Deze is bedoeld voor algemene interieurreiniging op panelen, naden en lastige plekken.',
        keywords: ['interieur', 'binnenkant', 'dashboard schoonmaken', 'panelen', 'kunststof schoon', 'vlekken interieur', 'middenconsole'],
      },
      'dash-restore': {
        priority: 79,
        reason: 'Die helpt om dof dashboard en kunststof weer verzorgd te laten ogen.',
        keywords: ['dashboard', 'dash', 'restore', 'dof', 'kunststof', 'plastic', 'verkleurd', 'opfrissen dashboard'],
      },
      'inside-refresh-kopie': {
        priority: 78,
        reason: 'Deze is vooral voor een frisser interieur en het neutraliseren van nare geuren.',
        keywords: ['geur', 'stank', 'fris', 'lucht', 'interieur geur', 'muffe geur', 'refresh', 'rooklucht'],
      },
      'intererior-cleaner-copy': {
        priority: 77,
        reason: 'Geschikt voor textiel, bekleding en stoffen oppervlakken die zichtbaar frisser moeten worden.',
        keywords: ['textiel', 'stof', 'bekleding', 'stoel', 'stoelen', 'mat', 'vloermat', 'vlek in stof', 'textile'],
      },
      'leather-cleaner': {
        priority: 76,
        reason: 'Deze is specifiek voor leer reinigen zonder onnodig agressief te werken.',
        keywords: ['leer', 'lederen', 'leder', 'leerreiniger', 'leather cleaner', 'stoel leer', 'leren stoel', 'leather'],
      },
      'diesel-injector': {
        priority: 70,
        reason: 'Past bij dieselmotoren als je het brandstofsysteem en de injectoren wilt ondersteunen.',
        keywords: ['diesel', 'injector', 'brandstof', 'motor diesel', 'diesel cleaner'],
      },
      'petrol-injector-cleaner': {
        priority: 69,
        reason: 'Past bij benzinemotoren als je injectoren en verbranding wilt ondersteunen.',
        keywords: ['benzine', 'petrol', 'injector', 'brandstof', 'motor benzine'],
      },
      'dpf-cleaner': {
        priority: 68,
        reason: 'Deze is gericht op DPF/roetfilter ondersteuning.',
        keywords: ['dpf', 'roetfilter', 'filter', 'regeneratie', 'diesel filter'],
      },
    };
  }

  normalize(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s-]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  addMessage(type, text) {
    if (!this.messages) return;

    const message = document.createElement('div');
    message.className = `dino-chatbot__message dino-chatbot__message--${type}`;

    const content = document.createElement('span');
    content.className = 'dino-chatbot__message-text';
    if (type === 'bot') {
      content.innerHTML = this.formatBotMessage(text);
    } else {
      content.textContent = text;
    }

    message.appendChild(content);
    this.messages.appendChild(message);
    this.scrollMessages();
    return message;
  }

  formatBotMessage(text) {
    const escaped = String(text || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

    return escaped.replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>');
  }

  addTypingIndicator() {
    if (!this.messages) return null;

    const indicator = document.createElement('div');
    indicator.className = 'dino-chatbot__message dino-chatbot__message--bot dino-chatbot__typing';
    indicator.setAttribute('aria-label', 'Dino is aan het typen');
    indicator.innerHTML = '<span></span><span></span><span></span>';
    this.messages.appendChild(indicator);
    this.scrollMessages();
    return indicator;
  }

  addProducts(products, message = null) {
    if (!this.messages) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'dino-chatbot__products';

    products.forEach((product) => {
      const card = document.createElement('article');
      card.className = 'dino-chatbot__product';

      const image = document.createElement('img');
      image.src = product.image;
      image.alt = product.title;
      image.loading = 'lazy';
      image.width = 74;
      image.height = 74;

      const content = document.createElement('div');
      const title = document.createElement('strong');
      title.textContent = product.title;

      const copy = document.createElement('p');
      copy.textContent = `${product.oneLiner || 'Past goed bij je vraag.'} Vanaf ${product.price}`;

      const actions = document.createElement('div');
      actions.className = 'dino-chatbot__product-actions';

      const link = document.createElement('a');
      link.href = product.url;
      link.textContent = 'Bekijk';

      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = product.available ? 'In winkelwagen' : 'Niet op voorraad';
      button.disabled = !product.available;
      button.setAttribute('aria-label', `${product.title} in winkelwagen`);
      button.addEventListener('click', () => this.addToCart(product, button));

      actions.append(link, button);
      content.append(title, copy, actions);
      card.append(image, content);
      wrapper.appendChild(card);
    });

    (message || this.messages).appendChild(wrapper);
    this.scrollMessages();
  }

  async addToCart(product, button) {
    if (!product.variantId || button.disabled) return;

    const previousText = button.textContent;
    button.disabled = true;
    button.textContent = 'Toevoegen...';

    try {
      const result = await this.addProductToCart(product);
      button.textContent = 'Toegevoegd';
      button.dataset.state = 'success';
      this.announce(`${product.title} is toegevoegd aan je winkelwagen.`);
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: result.cart,
            sourceId: 'dino-chatbot',
            data: {
              source: 'dino-chatbot',
              itemCount: result.cart.item_count,
              variantId: String(product.variantId),
              sections: result.sections,
            },
          },
        })
      );

      const cartDrawer = document.querySelector('cart-drawer-component');
      if (cartDrawer?.open) {
        this.close();
        window.setTimeout(() => cartDrawer.open(), 80);
      }

      window.setTimeout(() => {
        button.textContent = 'In winkelwagen';
        button.disabled = false;
        delete button.dataset.state;
      }, 2400);
    } catch (error) {
      button.textContent = error.message || 'Probeer opnieuw';
      button.dataset.state = 'error';
      this.announce(error.message || 'Toevoegen is niet gelukt. Probeer het opnieuw.');
      button.disabled = false;
      window.setTimeout(() => {
        button.textContent = previousText;
        delete button.dataset.state;
      }, 2200);
    }
  }

  async addProductToCart(product) {
    const cartAddUrl = `${window.Shopify?.routes?.root || '/'}cart/add.js`;
    const cartUrl = `${window.Shopify?.routes?.root || '/'}cart.js`;
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((element) => element.dataset.sectionId)
      .filter(Boolean);

    const response = await fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        items: [
          {
            id: Number(product.variantId),
            quantity: 1,
          },
        ],
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    });

    const added = await response.json();
    if (!response.ok || added.status) {
      throw new Error(added.description || added.message || 'Toevoegen is niet gelukt.');
    }

    const cartResponse = await fetch(cartUrl, {
      headers: {
        Accept: 'application/json',
      },
    });
    if (!cartResponse.ok) throw new Error('Winkelwagen kon niet worden bijgewerkt.');

    return {
      cart: await cartResponse.json(),
      sections: added.sections || {},
    };
  }

  announce(text) {
    const status = this.querySelector('[data-dino-status]');
    if (!status) return;

    status.textContent = '';
    window.requestAnimationFrame(() => {
      status.textContent = text;
    });
  }

  scrollMessages() {
    window.requestAnimationFrame(() => {
      this.messages.scrollTop = this.messages.scrollHeight;
    });
  }
}

if (!customElements.get('dino-chatbot')) {
  customElements.define('dino-chatbot', DinoChatbot);
}
